
import { Vue, Component, Watch } from "vue-property-decorator";
import { queries } from "@/apollo";
import * as legacyApollo from "@/apollo/legacySmartQuery";
import { User, DdPage, UserStakes } from "@/CodegenTypes";
import Clusterize from "clusterize.js";

import xlsx from "xlsx";

import { sumBy, startCase, first, sortBy } from "lodash";
import { formatDollar } from "@/helpers/formatDollar";
import { formatCount } from "@/helpers/formatCount";
import NumberStakeModal from "@/components/modules/bet-details/NumberStakeModal.vue";
import { customMoment } from "@/main";
import { useTranslation } from "@/store/translation";
import InputText from "primevue/inputtext/InputText";

type SortMode =
  | "number-asc"
  | "number-desc"
  | "inBig-asc"
  | "inBig-desc"
  | "inSmall-asc"
  | "inSmall-desc"
  | "inAmount-asc"
  | "inAmount-desc"
  | "intakeBig-asc"
  | "intakeBig-desc"
  | "intakeSmall-asc"
  | "intakeSmall-desc"
  | "intakeAmount-asc"
  | "intakeAmount-desc"
  | "outBig-asc"
  | "outBig-desc"
  | "outSmall-asc"
  | "outSmall-desc"
  | "outAmount-asc"
  | "outAmount-desc"
  | "maxPayout-asc"
  | "maxPayout-desc";

@Component({
  mixins: [legacyApollo.legacySmartQueries({
    user: {
      query: queries.user,
      variables() {
        return { userId: this.$route.query.userId };
      },
    },
    userStakes: {
      query: queries.userStakes,
      skip() {
        return !this.selectedReleaseDate;
      },
      fetchPolicy: "cache-and-network",
      variables() {
        return {
          releaseDate: customMoment(this.selectedReleaseDate)
            .startOf("day")
            .toDate(),
          behalfOf: this.$route.query.userId,
        };
      },
    },
  })],
  components: {
    NumberStakeModal,
  },
})
export default class UserStake extends Vue {
  user = null as null | User;

  numberFilter = "";

  selectedNumber = null as null | string;

  userStakes = null as null | UserStakes;

  showAccountSelectionModal = false;

  startCase = startCase;

  formatDollar = formatDollar;
  formatCount = formatCount;

  t = (text: string) => useTranslation().$t(text);

  selectedPage = null as null | DdPage;
  showNumberStakeModal = false;

  sortMode = "number-asc" as SortMode;

  clusterize = null as Clusterize | null;

  get renderedRows() {
    const rows = this.stakeRows.map((row, $index) => {
      return `
      <tr class="text-center">
        <td>${$index + 1}</td>
        <td>
              <a
                id="${row.number}"
                class="font-bold text-primary-600 cursor-pointer number-row"
                >${row.number}</a
              >
            </td>
            <td>${formatCount(row.bigIn)}</td>
            <td>${formatCount(row.smallIn)}</td>
            <td>${formatDollar(row.amountIn)}</td>
            <td>${formatCount(row.bigIntake)}</td>
            <td>${formatCount(row.smallIntake)}</td>
            <td>${formatDollar(row.amountIntake)}</td>
            <td>${formatCount(row.bigOut)}</td>
            <td>${formatCount(row.smallOut)}</td>
            <td>${formatDollar(row.amountOut)}</td>
            <td>${formatDollar(row.maxPayout)}</td>
      </tr>
      `;
    });
    return rows;
  }

  @Watch("stakeRows")
  doClusterize() {
    if (this.clusterize) {
      this.clusterize?.update(this.renderedRows);
    } else {
      this.clusterize = new Clusterize({
        rows: this.renderedRows,
        scrollId: "scrollArea",
        contentId: "contentArea",
        rows_in_block: 20,
        show_no_data_row: false,
        // blocks_in_cluster: 3,
        callbacks: {
          clusterChanged: () => {
            const numberRows = document.getElementsByClassName("number-row");
            for (const row of numberRows) {
              row.addEventListener("click", () => {
                this.handleSelectNumber(row.id);
              });
            }
          },
        },
      });
    }
  }

  mounted() {
    this.doClusterize();
  }

  get stakeRows() {
    if (!this.userStakes) {
      return [];
    }
    const downlineEatStake = this.userStakes.downlineEatStake;
    const downlineEatStakeMap = new Map();
    downlineEatStake.forEach((s) => {
      downlineEatStakeMap.set(s.number, s);
    });
    // const companyFinalStake = this.userStakes.companyFinalStake;
    const rows = this.userStakes.masterStake
      .filter((row) => row.number.includes(this.numberFilter))
      .map((row) => {
        const intakeRow = downlineEatStakeMap.get(row.number);
        // const outRow = companyFinalStake[$index];
        const bigIn = row.big;
        const smallIn = row.small;
        const amountIn = bigIn * 1.6 + smallIn * 0.7;
        const bigIntake = intakeRow.big;
        const smallIntake = intakeRow.small;
        const amountIntake = bigIntake * 1.6 + smallIntake * 0.7;

        const bigOut = bigIn - bigIntake;
        const smallOut = smallIn - smallIntake;
        const amountOut = amountIn - amountIntake;
        const maxPayout = bigIn * 4000 + smallIn * 3000;
        return {
          number: row.number,
          bigIn,
          smallIn,
          amountIn,
          bigIntake,
          smallIntake,
          amountIntake,
          bigOut,
          smallOut,
          amountOut,
          maxPayout,
        };
      });
    return sortBy(rows, (row) => {
      switch (this.sortMode) {
        case "number-asc":
          return row.number;
        case "number-desc":
          return -row.number;
        case "inBig-asc":
          return row.bigIn;
        case "inBig-desc":
          return -row.bigIn;
        case "inSmall-asc":
          return row.smallIn;
        case "inSmall-desc":
          return -row.smallIn;
        case "inAmount-asc":
          return row.amountIn;
        case "inAmount-desc":
          return -row.amountIn;
        case "intakeBig-asc":
          return row.bigIntake;
        case "intakeBig-desc":
          return -row.bigIntake;
        case "intakeSmall-asc":
          return row.smallIntake;
        case "intakeSmall-desc":
          return -row.smallIntake;
        case "intakeAmount-asc":
          return row.amountIntake;
        case "intakeAmount-desc":
          return -row.amountIntake;
        case "outBig-asc":
          return row.bigOut;
        case "outBig-desc":
          return -row.bigOut;
        case "outSmall-asc":
          return row.smallOut;
        case "outSmall-desc":
          return -row.smallOut;
        case "outAmount-asc":
          return row.amountOut;
        case "outAmount-desc":
          return -row.amountOut;
        case "maxPayout-asc":
          return row.maxPayout;
        case "maxPayout-desc":
          return -row.maxPayout;
      }
    });
  }

  get stakeSummary() {
    return {
      bigIn: sumBy(this.stakeRows, (r) => r.bigIn),
      smallIn: sumBy(this.stakeRows, (r) => r.smallIn),
      amountIn: sumBy(this.stakeRows, (r) => r.amountIn),
      bigIntake: sumBy(this.stakeRows, (r) => r.bigIntake),
      smallIntake: sumBy(this.stakeRows, (r) => r.smallIntake),
      amountIntake: sumBy(this.stakeRows, (r) => r.amountIntake),
      bigOut: sumBy(this.stakeRows, (r) => r.bigOut),
      smallOut: sumBy(this.stakeRows, (r) => r.smallOut),
      amountOut: sumBy(this.stakeRows, (r) => r.amountOut),
      // maxPayout: sumBy(this.stakeRows, (r) => r.maxPayout),
    };
  }

  get selectedReleaseDate() {
    const query = this.$route.query.startReleaseDate;
    return Array.isArray(query) ? first(query) : query;
  }

  formatDate(dateStr: string) {
    return customMoment(dateStr).format("DD MMM YYYY");
  }

  handleSelectNumber(number: string) {
    this.selectedNumber = number;
    this.showNumberStakeModal = true;
  }

  exportExcel() {
    if (!this.user || !this.stakeSummary) {
      return;
    }
    // const table = this.$refs.table;
    // const wb = xlsx.utils.table_to_book(table as HTMLElement, { raw: true });

    const wb = xlsx.utils.book_new();

    const ws_data = [] as string[][];
    const metaRow1 = [`Generated at ${customMoment()}`];
    ws_data.push(metaRow1);
    const metaRow2 = [
      `Account ${this.user.userId.toUpperCase()} ${this.formatDate(
        this.selectedReleaseDate || ""
      )} Stake`,
    ];
    ws_data.push(metaRow2);
    const headerRow1 = [
      "",
      "In",
      "In",
      "In",
      "Intake",
      "Intake",
      "Intake",
      "Out",
      "Out",
      "Out",
      "First Prize",
    ];
    ws_data.push(headerRow1);

    const headerRow2 = [
      "Number",
      "Big",
      "Small",
      "Amount",
      "Big",
      "Small",
      "Amount",
      "Big",
      "Small",
      "Amount",
      "Max Payout",
    ];
    ws_data.push(headerRow2);

    for (const row of this.stakeRows) {
      const rowData = [
        row.number,
        formatCount(row.bigIn),
        formatCount(row.smallIn),
        formatDollar(row.amountIn),
        formatCount(row.bigIntake),
        formatCount(row.smallIntake),
        formatDollar(row.amountIntake),
        formatCount(row.bigOut),
        formatCount(row.smallOut),
        formatDollar(row.amountOut),
        formatDollar(row.maxPayout),
      ];
      ws_data.push(rowData);
    }
    const footerData = [
      `Total (${this.stakeRows.length})`,
      formatCount(this.stakeSummary.bigIn),
      formatCount(this.stakeSummary.smallIn),
      formatDollar(this.stakeSummary.amountIn),
      formatCount(this.stakeSummary.bigIntake),
      formatCount(this.stakeSummary.smallIntake),
      formatDollar(this.stakeSummary.amountIntake),
      formatCount(this.stakeSummary.bigOut),
      formatCount(this.stakeSummary.smallOut),
      formatDollar(this.stakeSummary.amountOut),
      "",
      // formatDollar(this.stakeSummary.maxPayout),
    ];
    ws_data.push(footerData);

    const ws = xlsx.utils.aoa_to_sheet(ws_data);
    ws["!merges"] = [
      { s: { c: 0, r: 0 }, e: { c: 10, r: 0 } },
      { s: { c: 0, r: 1 }, e: { c: 10, r: 1 } },
      { s: { c: 1, r: 2 }, e: { c: 3, r: 2 } },
      { s: { c: 4, r: 2 }, e: { c: 6, r: 2 } },
      { s: { c: 7, r: 2 }, e: { c: 9, r: 2 } },
    ];
    xlsx.utils.book_append_sheet(wb, ws, "Sheet 1");

    xlsx.writeFile(wb, `${this.user?.userId.toUpperCase()}-stake.xlsx`, {
      bookType: "xlsx",
    });
    return;
  }
}
